import { useSeo } from "@/hooks/useSeo";
import { CAPABILITIES, MATERIALS } from "@/data/site";
import { Info } from "lucide-react";

const PageHero = ({ overline, title, subtitle }) => (
  <section className="border-b border-neutral-800 bg-neutral-900">
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-orange-500">{overline}</p>
      <h1 className="mt-3 font-heading text-4xl font-black tracking-tighter text-white sm:text-5xl">{title}</h1>
      {subtitle && <p className="mt-4 max-w-2xl text-neutral-400">{subtitle}</p>}
    </div>
  </section>
);

export default function Capabilities() {
  useSeo(
    "Capabilities | CNC Milling & Turning | Edgeworks Manufacturing",
    "CNC milling, CNC turning, prototypes, small production runs, replacement machine parts, and tooling. Materials include aluminum, steel, stainless, brass, and plastics."
  );

  return (
    <div>
      <PageHero
        overline="Capabilities"
        title="CNC Machining Capabilities"
        subtitle="Practical CNC milling and turning for custom machined parts — from a single prototype to repeatable small production runs."
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-px border border-neutral-800 bg-neutral-800 md:grid-cols-2">
          {CAPABILITIES.map((c, i) => (
            <div key={c.title} className="bg-neutral-950 p-7 lg:p-9" data-testid={`capability-${i}`}>
              <span className="font-mono text-sm text-orange-500">{String(i + 1).padStart(2, "0")}</span>
              <h2 className="mt-2 font-heading text-xl font-bold text-white">{c.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-neutral-400">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-neutral-800 bg-neutral-900">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-orange-500">Materials</p>
          <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Materials commonly machined
          </h2>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="materials-list">
            {MATERIALS.map((m) => (
              <span key={m} className="border border-neutral-700 bg-neutral-950 px-4 py-2 font-mono text-sm text-neutral-200">
                {m}
              </span>
            ))}
          </div>
          <div className="mt-10 flex items-start gap-3 border-l-2 border-orange-500 bg-neutral-950 p-5">
            <Info className="mt-0.5 shrink-0 text-orange-500" size={20} />
            <p className="text-sm text-neutral-300">
              Tolerance capability depends on material, geometry, setup, and inspection requirements.
              Send a drawing or model for review.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
